import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import { SkeletonCard } from '../components/SkeletonCard';
import toast from 'react-hot-toast';

const BADGE_META = {
  community_hero: { icon: '🦸', label: 'Community Hero', desc: '10+ reports submitted' },
  early_adopter:  { icon: '🌱', label: 'Early Adopter',  desc: 'Joined in beta' },
  top_reporter:   { icon: '📋', label: 'Top Reporter',   desc: '50+ verified reports' },
};

export default function ProfilePage() {
  const [user, setUser]   = useState(null);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/auth/me'), api.get('/saved')])
      .then(([u, s]) => { setUser(u.data.user); setSaved(s.data.saved); })
      .catch(() => { toast.error('Please sign in'); navigate('/login'); })
      .finally(() => setLoading(false));
  }, []);

  const unsave = async (chargerId) => {
    try {
      await api.delete(`/saved/${chargerId}`);
      setSaved(s => s.filter(c => c.id !== chargerId));
      toast.success('Removed');
    } catch (_) { toast.error('Failed'); }
  };

  const toggleNotify = async (chargerId, current) => {
    try {
      await api.patch(`/saved/${chargerId}`, { notify: !current });
      setSaved(s => s.map(c => c.id === chargerId ? { ...c, notify: !current } : c));
    } catch (_) { toast.error('Failed'); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) return toast.error('Passwords do not match');
    if (pwForm.next.length < 6) return toast.error('Min 6 characters');
    setPwLoading(true);
    try {
      await api.patch('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.next });
      toast.success('Password updated!');
      setPwForm({ current: '', next: '', confirm: '' });
      setShowPw(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setPwLoading(false); }
  };

  const copyReferral = () => {
    const url = `${window.location.origin}/signup?ref=${user.referral_code}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied!');
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', paddingTop: 72, padding: '72px 16px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
    </div>
  );

  const progress = Math.min((user?.report_count || 0) / 10, 1);

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    background: 'var(--navy)', border: '1px solid var(--border-2)',
    color: 'var(--text-1)', fontSize: 13, fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', paddingTop: 56, fontFamily: 'Inter, sans-serif' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg,var(--slate) 0%,var(--navy) 100%)', borderBottom: '1px solid var(--border)', padding: '28px 20px 24px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', gap: 18, alignItems: 'center' }}>
          <div style={{
            width: 60, height: 60, borderRadius: 18, flexShrink: 0,
            background: 'linear-gradient(135deg,#00D4AA,#0099FF)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 800, color: '#F0F4FF',
          }}>
            {(user?.name || user?.email || 'U')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-1)', marginBottom: 3, letterSpacing: '-0.3px' }}>
              {user?.name || 'EV Driver'}
            </h1>
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>{user?.email}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {user?.premium
                ? <span style={{ padding: '3px 10px', borderRadius: 10, background: 'var(--teal-dim)', color: 'var(--teal)', fontSize: 11, fontWeight: 700, border: '1px solid var(--teal-mid)' }}>⭐ PREMIUM</span>
                : <Link to="/premium" style={{ padding: '3px 10px', borderRadius: 10, background: 'var(--border)', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>Upgrade to Premium →</Link>
              }
              {user?.country && <span style={{ padding: '3px 8px', borderRadius: 10, background: 'var(--border)', color: 'var(--text-3)', fontSize: 11 }}>{user.country}</span>}
            </div>
          </div>
          <button onClick={() => { localStorage.removeItem('evapp_token'); navigate('/'); }} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-3)', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
            Log out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '20px 16px 60px' }}>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Points', value: user?.points || 0, color: 'var(--teal)' },
            { label: 'Reports', value: user?.report_count || 0, color: '#818CF8' },
            { label: 'Badges', value: user?.badges?.length || 0, color: 'var(--amber)' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: 'JetBrains Mono, monospace' }}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Free premium progress */}
        {!user?.premium && (
          <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Free Premium Progress</span>
              <span style={{ fontSize: 13, color: 'var(--teal)', fontFamily: 'JetBrains Mono, monospace' }}>{user?.report_count || 0}/10</span>
            </div>
            <div style={{ height: 6, background: 'var(--navy)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress * 100}%`, background: 'linear-gradient(90deg,#00D4AA,#0099FF)', borderRadius: 3, transition: 'width 0.6s ease' }} />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>
              {progress >= 1 ? 'Premium should have unlocked — contact support.' : `${10 - (user?.report_count || 0)} more reports to unlock Premium free`}
            </p>
          </div>
        )}

        {/* Referral */}
        <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>🎁 Referral Code</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>You and your friend both earn 20 points on signup</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--navy)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border-2)' }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 700, color: 'var(--teal)', letterSpacing: 4, flex: 1 }}>
              {user?.referral_code || '…'}
            </span>
            <button onClick={copyReferral} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: 'var(--teal)', color: '#F0F4FF', fontWeight: 600, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
              Copy Link
            </button>
          </div>
        </div>

        {/* Badges */}
        {user?.badges?.length > 0 && (
          <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Badges</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {user.badges.map(b => {
                const m = BADGE_META[b] || { icon: '🏅', label: b, desc: '' };
                return (
                  <div key={b} style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'var(--navy)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--amber)44' }}>
                    <span style={{ fontSize: 22 }}>{m.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{m.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Saved chargers */}
        <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Saved Chargers ({saved.length})</div>
          {saved.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-3)', fontSize: 14 }}>
              No saved chargers yet.<br />
              <span style={{ fontSize: 13 }}>Tap "Save & Alert" on any charger to track it here.</span>
            </div>
          ) : saved.map(c => (
            <div key={c.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderBottom: '1px solid var(--border)', gap: 10,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link to={`/charger/${c.slug}`} style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)', textDecoration: 'none', display: 'block', marginBottom: 3 }}>
                  {c.name}
                </Link>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  📍 {[c.city, c.country].filter(Boolean).join(', ')}
                  {c.power_kw ? ` · ${c.power_kw} kW` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <StatusBadge status={c.status} size="sm" />
                <button
                  onClick={() => toggleNotify(c.id, c.notify)}
                  title={c.notify ? 'Alerts on — click to disable' : 'Alerts off — click to enable'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, opacity: c.notify ? 1 : 0.35 }}
                >🔔</button>
                <button onClick={() => unsave(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            </div>
          ))}
        </div>

        {/* Change password */}
        <div style={{ background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Change Password</div>
            <button onClick={() => setShowPw(v => !v)} style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 7, padding: '5px 12px', color: 'var(--text-3)', fontSize: 12, cursor: 'pointer' }}>
              {showPw ? 'Cancel' : 'Change'}
            </button>
          </div>
          {showPw && (
            <form onSubmit={changePassword} style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Current password</label>
                <input type="password" required placeholder="Current password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>New password</label>
                <input type="password" required minLength={6} placeholder="Min. 6 characters" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Confirm new password</label>
                <input type="password" required placeholder="Repeat new password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} style={inputStyle} />
              </div>
              <button type="submit" disabled={pwLoading} style={{ padding: '10px 0', borderRadius: 9, border: 'none', background: 'var(--teal)', color: '#F0F4FF', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: pwLoading ? 0.7 : 1 }}>
                {pwLoading ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
